<template>
  <div class="w-full flex items-center justify-center gap-2">
    <div class="flex flex-col glass-frosted rounded-8xl w-83 h-67.5 p-4">
      <div class="flex-1 flex flex-col">
        <div class="flex justify-end">
          <Button
            color="glass"
            class="w-12 h-6 text-s-medium"
            :class="{
              'text-text-glass-disabled': !isCanEnabled,
            }"
            :disabled="!isCanEnabled"
            @click="handleTakeEffect"
          >
            {{ $t('keys.combinedKey.apply') }}
          </Button>
        </div>
        <div class="flex-1 flex justify-center items-center gap-4 mb-5">
          <template v-for="(key, index) in combinedKeys" :key="key.value">
            <span v-if="index !== 0" class="text-text-glass-default">+</span>
            <div
              :class="`relative flex flex-col items-center gap-2 ${selectedType === key.value ? 'text-black-white' : 'text-text-glass-default'}`"
              @dragover="(event: DragEvent) => handleDragOver(key.value, event)"
              @dragleave="() => handleDragLeave(key.value)"
              @drop="(event: DragEvent) => handleDrop(key.value, event)"
            >
              <div
                :class="`flex justify-center items-center w-14 h-14 px-0.5 text-black-white rounded-xl border border-solid bg-key cursor-pointer transition-all hover:border-blue-light-hover ${getSelectionButtonBorderClass(key.value)}`"
                @click="() => (selectedType = key.value)"
              >
                <span class="w-full text-center text-l-medium leading-4 line-clamp-2">
                  {{
                    getLocalizedField(
                      currentSelection[key.value]?.alias || currentSelection[key.value]?.name,
                    )
                  }}
                </span>
              </div>
              <span class="text-m-medium">{{ key.label }}</span>
              <Icon
                v-show="currentSelection[key.value]"
                icon="Function-Fill-Close"
                class="absolute -top-1 -right-1 w-4 h-4 text-brand-red cursor-pointer"
                @click.stop="clearSelection(key.value)"
              />
            </div>
          </template>
        </div>
      </div>
      <div class="flex items-center mt-2 text-text-glass-default">
        <Icon icon="Function-FiII-Description" class="w-4 h-4 mt-0.5 mr-0.5" />
        <span class="text-s-medium"> {{ $t('keys.combinedKey.description') }} </span>
      </div>
    </div>

    <div class="glass-frosted relative rounded-8xl w-fit h-67.5 p-4">
      <div class="p-2">
        <Keyboard
          key-class="flex items-center justify-center gap-1 bg-key text-black-white font-[Barlow] text-xs-semibold! whitespace-nowrap transition-colors cursor-pointer hover:text-text-glass-default active:bg-white active:text-brand-blue2 disabled:text-glass-disabled disabled:cursor-not-allowed disabled:pointer-events-none"
          :keyboard-config="keyboardConfig!"
          :native-event-names="sourceKeyboardEventNames"
          :gap="6"
          @key-event="handleSourceKeyEvent"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, inject, ref } from 'vue'
  import { Keyboard, Button, Icon, toast } from '@rapoo/element'
  import { setDragData, DragType, getDragData } from '@/utils/dnd'
  import { useDragImage } from '@/views/device/keyboard/keys/composables/useDragImage'
  import { fullSize108 } from '@/utils/fullSize108'
  import { KEYCODE_KEYWORDS_MAP, getLocalizedField, parseNumber } from '../utils/keyMapSetting'
  import { SdkKeyAttrType } from '../utils/constants'
  import { SelectedKeySymbol, UpdateSelectedKeyMappingSymbol } from '../types'
  import { useLocaleStore } from '@/stores'

  import type {
    KeyEventName,
    KeyboardKeyEventPayload,
    KeyboardConfig,
    KeyConfig,
  } from '@rapoo/element'
  import type { MouseKeys } from '@rapoo/device-sdk'
  import type { KeyChangePayload, UpdateSelectedKeyMapping } from '../types'

  type SelectionType = 'combo1' | 'combo2' | 'combo3'

  const SELECTION_ORDER: SelectionType[] = ['combo1', 'combo2', 'combo3']

  const selectedKey = inject(SelectedKeySymbol)
  const updateSelectedKeyMapping = inject<UpdateSelectedKeyMapping>(UpdateSelectedKeyMappingSymbol)
  const { t } = useLocaleStore()

  const keyboardConfig = computed<KeyboardConfig | null>(() => {
    return {
      ...fullSize108,
      keys: fullSize108.keys.map((row) =>
        row
          .map((key) => {
            const { w, h, x, y, keywords, ...rest } = key
            const config: KeyConfig = KEYCODE_KEYWORDS_MAP.get(keywords)!

            return {
              ...rest,
              ...config,
              rect: { w, h, x, y },
            }
          })
          .filter((key) => key.keywords !== 'Fn'),
      ),
    }
  })

  const sourceKeyboardEventNames: KeyEventName[] = ['click', 'dragstart', 'dragend']
  const { setDragImage } = useDragImage()

  const combinedKeys = computed<{ label: string; value: SelectionType }[]>(() => [
    { label: t('keys.combinedKey.key1'), value: 'combo1' },
    { label: t('keys.combinedKey.key2'), value: 'combo2' },
    { label: t('keys.combinedKey.key3'), value: 'combo3' },
  ])

  const selectedType = ref<SelectionType>('combo1')
  const dragOverType = ref<SelectionType | null>(null)

  const currentSelection = ref<Record<SelectionType, KeyConfig | null>>({
    combo1: null,
    combo2: null,
    combo3: null,
  })

  /** 至少有一个按键框被填充才能生效 */
  const isCanEnabled = computed(() =>
    SELECTION_ORDER.some((key) => currentSelection.value[key] != null),
  )

  /** 填充后自动跳转到下一个按键框 */
  const advanceToNext = (current: SelectionType) => {
    const currentIndex = SELECTION_ORDER.indexOf(current)
    const nextIndex = (currentIndex + 1) % SELECTION_ORDER.length
    selectedType.value = SELECTION_ORDER[nextIndex]
  }

  // 功能框高亮边框
  const getSelectionButtonBorderClass = (type: SelectionType) => {
    if (dragOverType.value === type || selectedType.value === type) return 'border-brand-blue1'
    return 'border-transparent'
  }

  const clearSelection = (type: SelectionType) => {
    currentSelection.value[type] = null
  }

  // 设置当前功能框内容并自动跳转
  const setSelection = (type: SelectionType, item: KeyConfig) => {
    currentSelection.value[type] = item
    advanceToNext(type)
  }

  // 功能框 拖拽进入
  const handleDragOver = (type: SelectionType, event: DragEvent) => {
    event.preventDefault()
    dragOverType.value = type
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy'
    }
  }

  // 功能框 拖拽离开
  const handleDragLeave = (type: SelectionType) => {
    if (dragOverType.value === type) {
      dragOverType.value = null
    }
  }

  // 功能框 拖拽放下
  const handleDrop = (type: SelectionType, event: DragEvent) => {
    event.preventDefault()
    dragOverType.value = null

    const dataTransfer = event.dataTransfer
    if (!dataTransfer) return
    const draggedData = getDragData(dataTransfer, DragType.KEY_MAPPING)
    if (draggedData) {
      setSelection(type, draggedData as KeyConfig)
    }
  }

  // 右侧键盘点击/拖拽
  const handleSourceKeyEvent = (ctx: KeyboardKeyEventPayload) => {
    const { data, eventName, nativeEvent } = ctx

    if (eventName === 'click') {
      setSelection(selectedType.value, data)
    }

    if (eventName === 'dragstart') {
      const event = nativeEvent as DragEvent
      const dataTransfer = event.dataTransfer
      if (!dataTransfer) return

      setDragData(dataTransfer, DragType.KEY_MAPPING, data as any)
      setDragImage(event, {
        text: getLocalizedField(data.alias || data.name),
      })
    }
  }

  /**
   * 点击"生效"：将当前选中的按键组合下发为自定义组合键。
   *
   * SDK payload 结构：
   * - type: 0x0d (ArbitraryCombined) — 无修饰键区分的任意组合
   * - code: [keyCode1, keyCode2, keyCode3] — 最多3个按键码
   *
   * 如果某些按键是修饰键（0xe0~0xe7），则使用 type=0x02 (CombinedKey) + combinKey 位数组。
   */
  const handleTakeEffect = () => {
    if (!isCanEnabled.value) {
      toast.error(t('toast.keys.atLeastOne'))
      return
    }

    if (!selectedKey?.value || !updateSelectedKeyMapping) return

    const keys = SELECTION_ORDER.map((slot) => currentSelection.value[slot]).filter(
      (k): k is KeyConfig => k != null,
    )

    if (!keys.length) return

    // 分离修饰键和普通键
    const modifierKeys: number[] = []
    const normalKeys: number[] = []

    for (const key of keys) {
      const code = parseNumber(key.code)
      if (code == null) continue

      // HID 修饰键码范围 0xe0~0xe7
      if (code >= 0xe0 && code <= 0xe7) {
        modifierKeys.push(code)
      } else {
        normalKeys.push(code)
      }
    }

    // 构造 payload
    let rawType: number
    let rawCode: number[]
    let rawCombinKey: MouseKeys.combinKey | undefined

    if (modifierKeys.length > 0) {
      // 有修饰键：使用 CombinedKey (0x02)
      rawType = SdkKeyAttrType.CombinedKey
      rawCode = [...normalKeys]
      // 补齐到至少2个元素（SDK 协议要求 code 数组长度为2）
      while (rawCode.length < 2) rawCode.push(0)

      rawCombinKey = [0, 0, 0, 0, 0, 0, 0, 0]
      for (const mCode of modifierKeys) {
        const bitIndex = mCode - 0xe0
        if (bitIndex >= 0 && bitIndex <= 7) {
          rawCombinKey[bitIndex] = 1
        }
      }
    } else {
      // 无修饰键：使用 ArbitraryCombined (0x0d)
      rawType = SdkKeyAttrType.ArbitraryCombined
      rawCode = [...normalKeys]
      // 补齐到3个元素
      while (rawCode.length < 3) rawCode.push(0)
    }

    const payload: KeyChangePayload = {
      keywords: selectedKey.value.keywords,
      keyType: 'combinedKey',
      target: null,
      raw: {
        type: rawType,
        code: rawCode,
        combinKey: rawCombinKey,
        combinedLabel: keys.map((k) => getLocalizedField(k.alias || k.name)).join('+'),
      },
    }

    updateSelectedKeyMapping(payload)

    // 重置选择
    currentSelection.value = { combo1: null, combo2: null, combo3: null }
    selectedType.value = 'combo1'
  }
</script>

<style scoped></style>
